// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Iter 44 — Google now redirects to /auth/callback?code=XXX (authorization
// code in QUERY STRING). We POST the code + the redirect_uri we originally
// used to the backend, which exchanges it for an id_token and returns our JWT.
// The legacy Emergent flow used a hash `#session_id=…` — still handled below
// as a fallback so browsers with a stale tab don't error out.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AuthCallback() {
    const navigate = useNavigate();
    const { exchangeGoogleSession } = useAuth();
    const handled = useRef(false);

    useEffect(() => {
        if (handled.current) return;
        handled.current = true;

        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const err = url.searchParams.get("error");

        // Legacy fallback — old Emergent-managed flow put `session_id` in the hash.
        const hash = window.location.hash || "";
        const sm = hash.match(/session_id=([^&]+)/);
        const legacySessionId = sm ? decodeURIComponent(sm[1]) : null;

        if (err) {
            toast.error(`Google sign-in cancelled: ${err}`);
            navigate("/login", { replace: true });
            return;
        }
        if (!code && !legacySessionId) {
            navigate("/login", { replace: true });
            return;
        }

        // Strip the sensitive code/hash from the URL so it doesn't linger in history.
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUri = window.location.origin + "/auth/callback";
        window.history.replaceState(null, "", window.location.pathname);

        (async () => {
            try {
                const payload = code ? { code, redirect_uri: redirectUri } : { session_id: legacySessionId };
                const user = await exchangeGoogleSession(payload);
                toast.success(`Welcome, ${user.name}!`);
                if (["super_admin", "admin"].includes(user.role)) {
                    navigate("/admin", { replace: true });
                } else if (user.role === "manager") {
                    navigate("/manager", { replace: true });
                } else {
                    navigate("/", { replace: true });
                }
            } catch (e) {
                const detail = e.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Google login failed");
                navigate("/login", { replace: true });
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
                <div className="w-12 h-12 border-2 border-[#00FF66] border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="mt-4 text-[#00FF66] font-display uppercase tracking-widest text-sm">Signing you in...</p>
            </div>
        </div>
    );
}
